// Extracted from the web lap-brief dependency graph. Shared by API and web.
import { type MyChronSessionExport } from './telemetrySession.js';
import { type ReplayLapWindow } from './telemetrySession.js';
import { type MyChronGpsPoint } from './telemetrySession.js';
import { gpsPointsInWindow } from './telemetrySession.js';
export type GpsCoord = { lat: number; lon: number };

export type StartLineSuggestion = {
  pointA: GpsCoord;
  pointB: GpsCoord;
  confidence: number;
};

export type CurveType =
  | 'hairpin'
  | 'sharp'
  | 'sweeper'
  | 'chicane'
  | 'kink'
  | 'complex'
  | 'unknown';

export const CURVE_TYPE_LABELS: Record<CurveType, string> = {
  hairpin: 'Hairpin',
  sharp: 'Sharp',
  sweeper: 'Sweeper',
  chicane: 'Chicane',
  kink: 'Kink',
  complex: 'Complex',
  unknown: 'Unknown',
};

export type CurveDetectionProfileConfig = {
  label: string;
  apexSmoothWindow: number;
  deflectionSmoothWindow: number;
  deflectionRadius: number;
  arcTurnRadius: number;
  minLocalCurvatureDeg: number;
  minArcTurnDeg: number;
  minDeflectionScore: number;
  minRawCurvatureDeg: number;
  minSeparationM: number;
  minChicaneKeepSeparationM: number;
  useSignedPeaks: boolean;
  candidateDedupDistanceM: number;
};

export type DetectableCurveType = Exclude<CurveType, 'complex' | 'unknown'>;

export const CURVE_DETECTION_PROFILES: Record<DetectableCurveType, CurveDetectionProfileConfig> = {
  hairpin: {
    label: 'Hairpin',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 3,
    deflectionRadius: 3,
    arcTurnRadius: 3,
    minLocalCurvatureDeg: 10,
    minArcTurnDeg: 28,
    minDeflectionScore: 24,
    minRawCurvatureDeg: 7,
    minSeparationM: 18,
    minChicaneKeepSeparationM: 8,
    useSignedPeaks: false,
    candidateDedupDistanceM: 5,
  },
  sharp: {
    label: 'Sharp',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 5,
    deflectionRadius: 4,
    arcTurnRadius: 4,
    minLocalCurvatureDeg: 8,
    minArcTurnDeg: 16,
    minDeflectionScore: 24,
    minRawCurvatureDeg: 6,
    minSeparationM: 26,
    minChicaneKeepSeparationM: 9,
    useSignedPeaks: false,
    candidateDedupDistanceM: 8,
  },
  sweeper: {
    label: 'Sweeper',
    apexSmoothWindow: 9,
    deflectionSmoothWindow: 11,
    deflectionRadius: 8,
    arcTurnRadius: 6,
    minLocalCurvatureDeg: 6,
    minArcTurnDeg: 14,
    minDeflectionScore: 20,
    minRawCurvatureDeg: 4,
    minSeparationM: 38,
    minChicaneKeepSeparationM: 12,
    useSignedPeaks: false,
    candidateDedupDistanceM: 14,
  },
  chicane: {
    label: 'Chicane',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 3,
    deflectionRadius: 2,
    arcTurnRadius: 2,
    minLocalCurvatureDeg: 7,
    minArcTurnDeg: 10,
    minDeflectionScore: 16,
    minRawCurvatureDeg: 5,
    minSeparationM: 18,
    minChicaneKeepSeparationM: 8,
    useSignedPeaks: true,
    candidateDedupDistanceM: 6,
  },
  kink: {
    label: 'Kink',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 3,
    deflectionRadius: 2,
    arcTurnRadius: 2,
    minLocalCurvatureDeg: 5,
    minArcTurnDeg: 8,
    minDeflectionScore: 14,
    minRawCurvatureDeg: 4,
    minSeparationM: 22,
    minChicaneKeepSeparationM: 8,
    useSignedPeaks: false,
    candidateDedupDistanceM: 6,
  },
};

const DETECTION_PROFILE_ORDER: DetectableCurveType[] = ['hairpin', 'sharp', 'chicane'];

const PROFILE_PRIORITY: Record<DetectableCurveType, number> = {
  hairpin: 5,
  sharp: 4,
  chicane: 3,
  sweeper: 2,
  kink: 1,
};

const GLOBAL_MIN_CORNER_SEPARATION_M = 24;

const CHICANE_PAIR_MIN_SEPARATION_M = 8;

const CHICANE_PAIR_MAX_SEPARATION_M = 22;

export type CornerApexSuggestion = {
  id: string;
  label: string;
  lat: number;
  lon: number;
  turnNumber: number;
  confidence: number;
  /** Classified bend geometry after detection. */
  curveType?: CurveType;
  /** Which detection profile first surfaced this apex. */
  detectionProfile?: DetectableCurveType;
};

export type CornerDetectionOptions = {
  kerbLines?: GpsCoord[][];
};

export function cornerKerbEvidenceScore(distanceM: number) {
  return 18 * Math.exp(-(distanceM ** 2) / (2 * 14 ** 2));
}

const EARTH_RADIUS_M = 6_371_000;

function haversineM(a: GpsCoord, b: GpsCoord) {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

function bearingDeg(from: GpsCoord, to: GpsCoord) {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLon = ((to.lon - from.lon) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function normalizeAngleDelta(deg: number) {
  const wrapped = ((((deg + 180) % 360) + 360) % 360) - 180;
  return Math.abs(wrapped);
}

function resamplePolylineByDistance(points: GpsCoord[], spacingM: number): GpsCoord[] {
  if (points.length < 2) {
    return points;
  }

  const cumulative = [0];
  for (let i = 1; i < points.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + haversineM(points[i - 1]!, points[i]!));
  }

  const total = cumulative.at(-1) ?? 0;
  if (total <= spacingM) {
    return points;
  }

  const result: GpsCoord[] = [points[0]!];
  let target = spacingM;
  let cursor = 0;

  while (target < total) {
    while (cursor < cumulative.length - 2 && cumulative[cursor + 1]! < target) {
      cursor += 1;
    }
    const start = cumulative[cursor]!;
    const end = cumulative[cursor + 1]!;
    const t = end > start ? (target - start) / (end - start) : 0;
    const a = points[cursor]!;
    const b = points[cursor + 1]!;
    result.push({
      lat: a.lat + (b.lat - a.lat) * t,
      lon: a.lon + (b.lon - a.lon) * t,
    });
    target += spacingM;
  }

  const last = points.at(-1)!;
  if (haversineM(result.at(-1)!, last) > 1) {
    result.push(last);
  }

  return result;
}

function smoothValues(values: number[], windowSize: number) {
  if (values.length === 0) {
    return values;
  }

  const half = Math.floor(windowSize / 2);
  return values.map((_, index) => {
    let sum = 0;
    let count = 0;
    for (let offset = -half; offset <= half; offset += 1) {
      const sample = values[index + offset];
      if (sample === undefined) {
        continue;
      }
      sum += sample;
      count += 1;
    }
    return count > 0 ? sum / count : 0;
  });
}

function curvatureDeg(a: GpsCoord, b: GpsCoord, c: GpsCoord) {
  const incoming = bearingDeg(a, b);
  const outgoing = bearingDeg(b, c);
  return normalizeAngleDelta(outgoing - incoming);
}

function signedCurvatureDeg(a: GpsCoord, b: GpsCoord, c: GpsCoord) {
  const incoming = bearingDeg(a, b);
  const outgoing = bearingDeg(b, c);
  return ((((outgoing - incoming + 180) % 360) + 360) % 360) - 180;
}

export function buildStartLineAtFraction(
  polyline: GpsCoord[],
  fraction: number,
  widthM = 24,
): StartLineSuggestion {
  const resampled = resamplePolylineByFraction(polyline, 240);
  const index = Math.min(
    resampled.length - 1,
    Math.max(0, Math.round(fraction * (resampled.length - 1))),
  );
  const center = resampled[index]!;
  const previous = resampled[Math.max(0, index - 2)] ?? center;
  const next = resampled[Math.min(resampled.length - 1, index + 2)] ?? center;
  const trackBearing = bearingDeg(previous, next);
  const perp = (trackBearing + 90) % 360;
  const rad = (perp * Math.PI) / 180;
  const half = widthM / 2;
  const dLat = ((half * Math.cos(rad)) / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLon =
    ((half * Math.sin(rad)) / (EARTH_RADIUS_M * Math.cos((center.lat * Math.PI) / 180))) *
    (180 / Math.PI);

  return {
    pointA: { lat: center.lat - dLat, lon: center.lon - dLon },
    pointB: { lat: center.lat + dLat, lon: center.lon + dLon },
    confidence: 0.95,
  };
}

function sumCurvatureWindow(smoothed: number[], center: number, radius: number) {
  let sum = 0;
  for (
    let index = Math.max(0, center - radius);
    index <= Math.min(smoothed.length - 1, center + radius);
    index += 1
  ) {
    sum += smoothed[index] ?? 0;
  }
  return sum;
}

function distanceAlongPolyline(points: GpsCoord[], fromIndex: number, toIndex: number) {
  if (toIndex <= fromIndex) {
    return 0;
  }

  let distance = 0;
  for (let index = fromIndex + 1; index <= toIndex; index += 1) {
    distance += haversineM(points[index - 1]!, points[index]!);
  }
  return distance;
}

function isLocalPeak(values: number[], index: number, radius = 2) {
  const value = values[index] ?? 0;
  for (let offset = -radius; offset <= radius; offset += 1) {
    if (offset === 0) {
      continue;
    }
    if ((values[index + offset] ?? 0) > value) {
      return false;
    }
  }
  return true;
}

function smoothIndexForResampled(resampledIndex: number) {
  return Math.max(0, resampledIndex - 1);
}

function localCurvatureAt(smoothed: number[], resampledIndex: number) {
  return smoothed[Math.min(smoothed.length - 1, smoothIndexForResampled(resampledIndex))] ?? 0;
}

type TypedCornerCandidate = {
  resampledIndex: number;
  score: number;
  localCurvature: number;
  detectionProfile: DetectableCurveType;
  signedCurvature: number;
};

type ProfileSignals = {
  apex: number[];
  deflection: number[];
  signed: number[];
};

function buildProfileSignals(
  curvatures: number[],
  signedCurvatures: number[],
  profile: DetectableCurveType,
): ProfileSignals {
  const config = CURVE_DETECTION_PROFILES[profile];
  const unsignedSource = curvatures;
  const signedSource = signedCurvatures.map((value) => Math.abs(value));
  const apexSource = config.useSignedPeaks ? signedSource : unsignedSource;
  const apex = smoothValues(apexSource, config.apexSmoothWindow);
  const deflectionBase = smoothValues(apexSource, config.deflectionSmoothWindow);
  const deflection = deflectionBase.map((_, index) =>
    sumCurvatureWindow(deflectionBase, index, config.deflectionRadius),
  );

  return { apex, deflection, signed: signedCurvatures };
}

function collectProfileCandidates(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  profile: DetectableCurveType,
): TypedCornerCandidate[] {
  const config = CURVE_DETECTION_PROFILES[profile];
  const signals = buildProfileSignals(curvatures, signedCurvatures, profile);
  const { apex, deflection } = signals;
  const deflectionRadius = config.deflectionRadius;
  const candidates: TypedCornerCandidate[] = [];

  for (let i = deflectionRadius; i < apex.length - deflectionRadius; i += 1) {
    const localCurvature = apex[i] ?? 0;
    const rawCurvature = config.useSignedPeaks
      ? Math.abs(signedCurvatures[i] ?? 0)
      : (curvatures[i] ?? 0);
    const deflectionScore = deflection[i] ?? 0;
    const arcTurn = sumCurvatureWindow(apex, i, config.arcTurnRadius);
    const resampledIndex = i + 1;

    const isCurvaturePeak = localCurvature >= config.minLocalCurvatureDeg && isLocalPeak(apex, i);
    const isDeflectionPeak =
      deflectionScore >= config.minDeflectionScore && isLocalPeak(deflection, i);
    if (!isCurvaturePeak && !isDeflectionPeak) {
      continue;
    }

    if (
      rawCurvature < config.minRawCurvatureDeg &&
      localCurvature < config.minLocalCurvatureDeg + 1
    ) {
      continue;
    }

    if (arcTurn < config.minArcTurnDeg) {
      continue;
    }

    if (
      localCurvature < config.minLocalCurvatureDeg + 2 &&
      deflectionScore < config.minDeflectionScore + 6
    ) {
      continue;
    }

    candidates.push({
      resampledIndex,
      score: deflectionScore + localCurvature * 0.65,
      localCurvature,
      detectionProfile: profile,
      signedCurvature: signedCurvatures[i] ?? 0,
    });
  }

  return candidates;
}

function collectSweeperRunCandidates(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
): TypedCornerCandidate[] {
  const config = CURVE_DETECTION_PROFILES.sweeper;
  const signals = buildProfileSignals(curvatures, signedCurvatures, 'sweeper');
  const { apex } = signals;
  const candidates: TypedCornerCandidate[] = [];
  let runStart = -1;

  for (let i = 0; i < apex.length; i += 1) {
    if ((apex[i] ?? 0) >= config.minLocalCurvatureDeg - 0.5) {
      if (runStart < 0) {
        runStart = i;
      }
    } else if (runStart >= 0) {
      if (i - runStart >= 4) {
        let best = runStart;
        for (let j = runStart; j < i; j += 1) {
          if ((apex[j] ?? 0) > (apex[best] ?? 0)) {
            best = j;
          }
        }
        const localCurvature = apex[best] ?? 0;
        const arcTurn = sumCurvatureWindow(apex, best, config.arcTurnRadius);
        if (localCurvature >= config.minLocalCurvatureDeg && arcTurn >= config.minArcTurnDeg) {
          candidates.push({
            resampledIndex: best + 1,
            score: arcTurn + localCurvature,
            localCurvature,
            detectionProfile: 'sweeper',
            signedCurvature: signedCurvatures[best] ?? 0,
          });
        }
      }
      runStart = -1;
    }
  }

  return candidates;
}

function dedupeTypedCandidates(
  resampled: GpsCoord[],
  candidates: TypedCornerCandidate[],
): TypedCornerCandidate[] {
  const sorted = [...candidates].sort((left, right) => left.resampledIndex - right.resampledIndex);
  const deduped: TypedCornerCandidate[] = [];
  const crossProfileDedupM = 14;

  for (const candidate of sorted) {
    const last = deduped.at(-1);
    if (
      last &&
      distanceAlongPolyline(resampled, last.resampledIndex, candidate.resampledIndex) <
        crossProfileDedupM
    ) {
      const lastPriority = PROFILE_PRIORITY[last.detectionProfile];
      const candidatePriority = PROFILE_PRIORITY[candidate.detectionProfile];
      if (
        candidatePriority > lastPriority ||
        (candidatePriority === lastPriority && candidate.score > last.score)
      ) {
        deduped[deduped.length - 1] = candidate;
      }
      continue;
    }
    deduped.push(candidate);
  }

  return deduped;
}

function isChicanePairSeparation(
  resampled: GpsCoord[],
  signedCurvatures: number[],
  fromIndex: number,
  toIndex: number,
) {
  const separation = distanceAlongPolyline(resampled, fromIndex, toIndex);
  if (separation < CHICANE_PAIR_MIN_SEPARATION_M || separation > CHICANE_PAIR_MAX_SEPARATION_M) {
    return false;
  }

  const fromSigned =
    signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, fromIndex - 1))] ?? 0;
  const toSigned =
    signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, toIndex - 1))] ?? 0;
  return (
    Math.sign(fromSigned) !== 0 &&
    Math.sign(toSigned) !== 0 &&
    Math.sign(fromSigned) !== Math.sign(toSigned)
  );
}

function selectPeaksFromTypedCandidates(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  candidates: TypedCornerCandidate[],
): {
  peakIndices: number[];
  peakScores: number[];
  peakProfiles: Map<number, DetectableCurveType>;
} {
  const peakIndices: number[] = [];
  const peakScores: number[] = [];
  const peakProfiles = new Map<number, DetectableCurveType>();

  for (const candidate of candidates) {
    const config = CURVE_DETECTION_PROFILES[candidate.detectionProfile];

    if (peakIndices.length === 0) {
      peakIndices.push(candidate.resampledIndex);
      peakScores.push(candidate.score);
      peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
      continue;
    }

    const lastIndex = peakIndices.length - 1;
    const lastPeak = peakIndices[lastIndex]!;
    const separation = distanceAlongPolyline(resampled, lastPeak, candidate.resampledIndex);
    const minSeparationM = Math.max(config.minSeparationM, GLOBAL_MIN_CORNER_SEPARATION_M);
    const oppositeSignChicane = isChicanePairSeparation(
      resampled,
      signedCurvatures,
      lastPeak,
      candidate.resampledIndex,
    );

    if (separation < minSeparationM) {
      if (oppositeSignChicane) {
        peakIndices.push(candidate.resampledIndex);
        peakScores.push(candidate.score);
        peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
        continue;
      }

      const lastProfile = peakProfiles.get(lastPeak) ?? 'sharp';
      const lastSignals = buildProfileSignals(curvatures, signedCurvatures, lastProfile);
      const candidateSignals = buildProfileSignals(
        curvatures,
        signedCurvatures,
        candidate.detectionProfile,
      );
      const lastCurvature = localCurvatureAt(lastSignals.apex, lastPeak);
      const candidateCurvature = localCurvatureAt(candidateSignals.apex, candidate.resampledIndex);
      const lastRaw = curvatures[Math.max(0, Math.min(curvatures.length - 1, lastPeak - 1))] ?? 0;
      const candidateRaw =
        curvatures[Math.max(0, Math.min(curvatures.length - 1, candidate.resampledIndex - 1))] ?? 0;

      if (
        candidateCurvature > lastCurvature ||
        candidateRaw > lastRaw + 1.5 ||
        candidate.score > (peakScores[lastIndex] ?? 0)
      ) {
        peakIndices[lastIndex] = candidate.resampledIndex;
        peakScores[lastIndex] = candidate.score;
        peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
      }
      continue;
    }

    peakIndices.push(candidate.resampledIndex);
    peakScores.push(candidate.score);
    peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
  }

  return { peakIndices, peakScores, peakProfiles };
}

function measureArcLengthM(resampled: GpsCoord[], centerIndex: number, halfWindow: number) {
  const start = Math.max(0, centerIndex - halfWindow);
  const end = Math.min(resampled.length - 1, centerIndex + halfWindow);
  return distanceAlongPolyline(resampled, start, end);
}

export function classifyCurveAtPeak(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  peakIndex: number,
  prevPeakIndex?: number,
  nextPeakIndex?: number,
): CurveType {
  const smoothIndex = Math.max(0, Math.min(curvatures.length - 1, peakIndex - 1));
  const local = curvatures[smoothIndex] ?? 0;
  const signed = signedCurvatures[smoothIndex] ?? 0;
  const arcTurn = sumCurvatureWindow(smoothValues(curvatures, 5), smoothIndex, 5);
  const arcLengthM = measureArcLengthM(resampled, peakIndex, 10);

  if (prevPeakIndex !== undefined) {
    const sepPrev = distanceAlongPolyline(resampled, prevPeakIndex, peakIndex);
    const prevSigned =
      signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, prevPeakIndex - 1))] ?? 0;
    if (
      sepPrev >= 6 &&
      sepPrev <= 28 &&
      Math.sign(prevSigned) !== 0 &&
      Math.sign(signed) !== 0 &&
      Math.sign(prevSigned) !== Math.sign(signed)
    ) {
      return 'chicane';
    }
  }

  if (nextPeakIndex !== undefined) {
    const sepNext = distanceAlongPolyline(resampled, peakIndex, nextPeakIndex);
    const nextSigned =
      signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, nextPeakIndex - 1))] ?? 0;
    if (
      sepNext >= 6 &&
      sepNext <= 28 &&
      Math.sign(nextSigned) !== 0 &&
      Math.sign(signed) !== 0 &&
      Math.sign(nextSigned) !== Math.sign(signed)
    ) {
      return 'chicane';
    }
  }

  if (local >= 14 && arcTurn >= 42) {
    return 'hairpin';
  }

  if (arcLengthM >= 50 && local <= 9 && arcTurn >= 14) {
    return 'sweeper';
  }

  if (local < 6 && arcTurn < 10) {
    return 'kink';
  }

  if (local >= 8 || arcTurn >= 20) {
    return 'sharp';
  }

  return 'complex';
}

function consolidateCornerPeaks(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  apexCurvature: number[],
  peakIndices: number[],
  peakScores: number[],
  peakProfiles: Map<number, DetectableCurveType>,
  minSeparationM = GLOBAL_MIN_CORNER_SEPARATION_M,
) {
  if (peakIndices.length <= 1) {
    return { peakIndices, peakScores, peakProfiles };
  }

  const scoreByIndex = new Map<number, number>();
  peakIndices.forEach((index, position) => {
    scoreByIndex.set(index, peakScores[position] ?? 0);
  });

  const sorted = [...peakIndices].sort((left, right) => left - right);
  const kept: number[] = [];

  for (const index of sorted) {
    const lastKept = kept.at(-1);
    if (lastKept === undefined) {
      kept.push(index);
      continue;
    }

    const separation = distanceAlongPolyline(resampled, lastKept, index);
    if (isChicanePairSeparation(resampled, signedCurvatures, lastKept, index)) {
      kept.push(index);
      continue;
    }

    if (separation < minSeparationM) {
      const lastScore = scoreByIndex.get(lastKept) ?? 0;
      const currentScore = scoreByIndex.get(index) ?? 0;
      const lastCurvature = localCurvatureAt(apexCurvature, lastKept);
      const currentCurvature = localCurvatureAt(apexCurvature, index);
      if (currentCurvature > lastCurvature || currentScore > lastScore) {
        peakProfiles.delete(lastKept);
        kept[kept.length - 1] = index;
        scoreByIndex.set(index, Math.max(currentScore, lastScore));
      }
      continue;
    }

    kept.push(index);
  }

  return {
    peakIndices: kept,
    peakScores: kept.map((index) => scoreByIndex.get(index) ?? 0),
    peakProfiles,
  };
}

function mergeNearbyCornerComplexes(
  resampled: GpsCoord[],
  apexCurvature: number[],
  peakIndices: number[],
  peakScores: number[],
  maxComplexSeparationM = 38,
  resampleSpacingM = 3,
) {
  if (peakIndices.length < 2) {
    return { peakIndices, peakScores };
  }

  const mergedIndices: number[] = [];
  const mergedScores: number[] = [];

  let clusterStart = 0;
  while (clusterStart < peakIndices.length) {
    let clusterEnd = clusterStart;
    while (
      clusterEnd + 1 < peakIndices.length &&
      distanceAlongPolyline(resampled, peakIndices[clusterEnd]!, peakIndices[clusterEnd + 1]!) <=
        maxComplexSeparationM
    ) {
      clusterEnd += 1;
    }

    if (clusterEnd === clusterStart) {
      mergedIndices.push(peakIndices[clusterStart]!);
      mergedScores.push(peakScores[clusterStart] ?? 0);
    } else {
      const clusterFrom = peakIndices[clusterStart]!;
      const clusterTo = peakIndices[clusterEnd]!;
      const searchEnd = Math.min(
        resampled.length - 1,
        clusterTo + Math.max(4, Math.ceil(18 / resampleSpacingM)),
      );

      let bestIndex = clusterFrom;
      let bestCurvature = localCurvatureAt(apexCurvature, clusterFrom);
      let bestScore = peakScores[clusterStart] ?? 0;

      for (let index = clusterFrom; index <= searchEnd; index += 1) {
        const curvature = localCurvatureAt(apexCurvature, index);
        const score = peakScores[clusterStart] ?? 0;
        if (curvature > bestCurvature || (curvature === bestCurvature && score > bestScore)) {
          bestCurvature = curvature;
          bestIndex = index;
          bestScore = score;
        }
      }

      mergedIndices.push(bestIndex);
      mergedScores.push(bestScore);
    }

    clusterStart = clusterEnd + 1;
  }

  return { peakIndices: mergedIndices, peakScores: mergedScores };
}

function gapDistanceAlongLoop(resampled: GpsCoord[], startPeak: number, endPeak: number) {
  if (endPeak > startPeak) {
    return distanceAlongPolyline(resampled, startPeak, endPeak);
  }

  const tail = distanceAlongPolyline(resampled, startPeak, resampled.length - 1);
  const head = distanceAlongPolyline(resampled, 0, endPeak);
  return tail + head;
}

function distanceAlongLoopBetween(resampled: GpsCoord[], fromIndex: number, toIndex: number) {
  if (fromIndex === toIndex) {
    return 0;
  }

  if (fromIndex < toIndex) {
    return distanceAlongPolyline(resampled, fromIndex, toIndex);
  }

  return (
    distanceAlongPolyline(resampled, fromIndex, resampled.length - 1) +
    distanceAlongPolyline(resampled, 0, toIndex)
  );
}

function nearestLoopSeparationM(
  resampled: GpsCoord[],
  peakIndices: number[],
  candidateIndex: number,
) {
  return peakIndices.reduce((min, peak) => {
    const forward = distanceAlongLoopBetween(resampled, peak, candidateIndex);
    const backward = distanceAlongLoopBetween(resampled, candidateIndex, peak);
    return Math.min(min, forward, backward);
  }, Number.POSITIVE_INFINITY);
}

function isIndexInGap(resampledIndex: number, startPeak: number, endPeak: number) {
  if (endPeak > startPeak) {
    return resampledIndex > startPeak && resampledIndex < endPeak;
  }

  return resampledIndex > startPeak || resampledIndex < endPeak;
}

function distanceInGapFromStart(
  resampled: GpsCoord[],
  startPeak: number,
  endPeak: number,
  resampledIndex: number,
) {
  if (endPeak > startPeak) {
    return distanceAlongPolyline(resampled, startPeak, resampledIndex);
  }

  if (resampledIndex >= startPeak) {
    return distanceAlongPolyline(resampled, startPeak, resampledIndex);
  }

  return (
    distanceAlongPolyline(resampled, startPeak, resampled.length - 1) +
    distanceAlongPolyline(resampled, 0, resampledIndex)
  );
}

function distanceInGapToEnd(
  resampled: GpsCoord[],
  startPeak: number,
  endPeak: number,
  resampledIndex: number,
) {
  if (endPeak > startPeak) {
    return distanceAlongPolyline(resampled, resampledIndex, endPeak);
  }

  if (resampledIndex <= endPeak) {
    return distanceAlongPolyline(resampled, resampledIndex, endPeak);
  }

  return (
    distanceAlongPolyline(resampled, resampledIndex, resampled.length - 1) +
    distanceAlongPolyline(resampled, 0, endPeak)
  );
}

function supplementSignedCurvaturePeaks(
  resampled: GpsCoord[],
  signedCurvatures: number[],
  peakIndices: number[],
  minGapDistanceM = 38,
  minSignedDeg = 9,
  minNeighborSeparationM = 6,
  maxSupplementsPerGap = 4,
) {
  const absSigned = signedCurvatures.map((value) => Math.abs(value));
  const sortedPeaks = [...peakIndices].sort((left, right) => left - right);
  const supplemented = new Set(sortedPeaks);

  for (let peakIndex = 0; peakIndex < sortedPeaks.length; peakIndex += 1) {
    const startPeak = sortedPeaks[peakIndex]!;
    const endPeak = sortedPeaks[(peakIndex + 1) % sortedPeaks.length]!;
    const gapDistance = gapDistanceAlongLoop(resampled, startPeak, endPeak);
    if (gapDistance < minGapDistanceM) {
      continue;
    }

    const gapCandidates: Array<{ resampledIndex: number; magnitude: number }> = [];

    for (let i = 2; i < signedCurvatures.length - 2; i += 1) {
      const resampledIndex = i + 1;
      if (!isIndexInGap(resampledIndex, startPeak, endPeak)) {
        continue;
      }

      const magnitude = absSigned[i] ?? 0;
      if (magnitude < minSignedDeg || !isLocalPeak(absSigned, i)) {
        continue;
      }

      const fromStart = distanceInGapFromStart(resampled, startPeak, endPeak, resampledIndex);
      const toEnd = distanceInGapToEnd(resampled, startPeak, endPeak, resampledIndex);
      if (fromStart < minNeighborSeparationM || toEnd < minNeighborSeparationM) {
        continue;
      }

      gapCandidates.push({ resampledIndex, magnitude });
    }

    gapCandidates.sort((left, right) => right.magnitude - left.magnitude);

    if (gapCandidates.length >= 2) {
      gapCandidates.slice(0, maxSupplementsPerGap).forEach((candidate) => {
        supplemented.add(candidate.resampledIndex);
      });
    } else if (gapCandidates.length === 1 && gapCandidates[0]!.magnitude >= 7) {
      supplemented.add(gapCandidates[0]!.resampledIndex);
    }
  }

  return [...supplemented].sort((left, right) => left - right);
}

function fillLargeGapsWithApexPeaks(
  resampled: GpsCoord[],
  apexCurvature: number[],
  rawCurvatures: number[],
  peakIndices: number[],
  minGapDistanceM = 38,
  segmentSpacingM = 30,
  minApexDeg = 3.5,
  minSeparationM = 10,
  maxAdditionsPerGap = 10,
) {
  if (peakIndices.length < 2) {
    return peakIndices;
  }

  const filled = new Set(peakIndices);
  const sortedPeaks = [...peakIndices].sort((left, right) => left - right);

  for (let peakIndex = 0; peakIndex < sortedPeaks.length; peakIndex += 1) {
    const startPeak = sortedPeaks[peakIndex]!;
    const endPeak = sortedPeaks[(peakIndex + 1) % sortedPeaks.length]!;
    const gapDistance = gapDistanceAlongLoop(resampled, startPeak, endPeak);
    if (gapDistance < minGapDistanceM) {
      continue;
    }

    const segmentCount = Math.min(
      maxAdditionsPerGap,
      Math.max(1, Math.ceil(gapDistance / segmentSpacingM)),
    );
    let addedInGap = 0;

    for (let segment = 1; segment <= segmentCount; segment += 1) {
      const segStartDist = (gapDistance * (segment - 1)) / segmentCount;
      const segEndDist = (gapDistance * segment) / segmentCount;

      let bestIndex = -1;
      let bestCurvature = 0;

      for (let i = 0; i < apexCurvature.length; i += 1) {
        const resampledIndex = i + 1;
        if (!isIndexInGap(resampledIndex, startPeak, endPeak)) {
          continue;
        }

        const fromStart = distanceInGapFromStart(resampled, startPeak, endPeak, resampledIndex);
        if (fromStart < segStartDist || fromStart >= segEndDist) {
          continue;
        }

        const curvature = Math.max(apexCurvature[i] ?? 0, rawCurvatures[i] ?? 0);
        if (curvature > bestCurvature) {
          bestCurvature = curvature;
          bestIndex = resampledIndex;
        }
      }

      const threshold = gapDistance > 70 ? 2.5 : minApexDeg;
      if (bestIndex < 0 || bestCurvature < threshold) {
        continue;
      }

      const nearestSeparation = nearestLoopSeparationM(resampled, [...filled], bestIndex);
      if (nearestSeparation < minSeparationM) {
        continue;
      }

      filled.add(bestIndex);
      addedInGap += 1;
    }

    // Large gaps that are mostly straight still need at least one marker.
    if (addedInGap === 0 && gapDistance > 65) {
      let bestIndex = -1;
      let bestCurvature = 0;

      for (let i = 0; i < apexCurvature.length; i += 1) {
        const resampledIndex = i + 1;
        if (!isIndexInGap(resampledIndex, startPeak, endPeak)) {
          continue;
        }

        const curvature = Math.max(apexCurvature[i] ?? 0, rawCurvatures[i] ?? 0);
        if (curvature > bestCurvature) {
          bestCurvature = curvature;
          bestIndex = resampledIndex;
        }
      }

      if (bestIndex >= 0 && bestCurvature >= 2) {
        const nearestSeparation = nearestLoopSeparationM(resampled, [...filled], bestIndex);
        if (nearestSeparation >= minSeparationM) {
          filled.add(bestIndex);
        }
      }
    }
  }

  return [...filled].sort((left, right) => left - right);
}

export function detectCornersFromPolyline(
  polyline: GpsCoord[],
  speedByIndex?: number[],
  options: CornerDetectionOptions = {},
): CornerApexSuggestion[] {
  // 2 m spacing — balance resolution vs. cluster noise.
  const resampleSpacingM = 2;
  const resampled = resamplePolylineByDistance(polyline, resampleSpacingM);
  if (resampled.length < 12) {
    return [];
  }

  const curvatures: number[] = [];
  for (let i = 1; i < resampled.length - 1; i += 1) {
    curvatures.push(curvatureDeg(resampled[i - 1]!, resampled[i]!, resampled[i + 1]!));
  }

  const signedCurvatures: number[] = [];
  for (let i = 1; i < resampled.length - 1; i += 1) {
    signedCurvatures.push(signedCurvatureDeg(resampled[i - 1]!, resampled[i]!, resampled[i + 1]!));
  }

  // Default sharp profile signals — used for merge, gap fill, and apex refinement.
  const sharpSignals = buildProfileSignals(curvatures, signedCurvatures, 'sharp');
  const { apex: apexCurvature, deflection } = sharpSignals;
  const smoothed = smoothValues(curvatures, CURVE_DETECTION_PROFILES.sharp.deflectionSmoothWindow);
  const kerbCenterIndices = (options.kerbLines ?? [])
    .filter((line) => line.length > 0)
    .map((line) => line[Math.floor(line.length / 2)]!)
    .map((kerbCenter) =>
      resampled.reduce(
        (bestIndex, point, index) =>
          haversineM(point, kerbCenter) < haversineM(resampled[bestIndex]!, kerbCenter)
            ? index
            : bestIndex,
        0,
      ),
    );
  const kerbEvidence = resampled.map((_, index) => {
    const nearestKerbDistanceM = kerbCenterIndices.reduce((nearest, kerbIndex) => {
      const sampleDistance = Math.abs(index - kerbIndex) * resampleSpacingM;
      const wrappedDistance = (resampled.length - Math.abs(index - kerbIndex)) * resampleSpacingM;
      return Math.min(nearest, sampleDistance, wrappedDistance);
    }, Number.POSITIVE_INFINITY);
    return Number.isFinite(nearestKerbDistanceM)
      ? cornerKerbEvidenceScore(nearestKerbDistanceM)
      : 0;
  });
  const scoreAtPeak = (peakIndex: number) => {
    const signalIndex = Math.max(0, Math.min(apexCurvature.length - 1, peakIndex - 1));
    return (
      (deflection[signalIndex] ?? 0) +
      (apexCurvature[signalIndex] ?? 0) * 0.65 +
      (kerbEvidence[peakIndex] ?? 0)
    );
  };

  const typedCandidates: TypedCornerCandidate[] = [];
  for (const profile of DETECTION_PROFILE_ORDER) {
    typedCandidates.push(
      ...collectProfileCandidates(resampled, curvatures, signedCurvatures, profile),
    );
  }

  const dedupedCandidates = dedupeTypedCandidates(resampled, typedCandidates);
  const selected = selectPeaksFromTypedCandidates(
    resampled,
    curvatures,
    signedCurvatures,
    dedupedCandidates,
  );
  let peakIndices = [...selected.peakIndices];
  let peakScores = [...selected.peakScores];
  const peakProfiles = new Map(selected.peakProfiles);

  const chicanePeaks = peakIndices.filter((index) => peakProfiles.get(index) === 'chicane');
  const mergeablePeaks = peakIndices.filter((index) => peakProfiles.get(index) !== 'chicane');
  const mergeableScores = mergeablePeaks.map((index) => {
    const position = peakIndices.indexOf(index);
    return peakScores[position] ?? 0;
  });

  const merged = mergeNearbyCornerComplexes(
    resampled,
    apexCurvature,
    mergeablePeaks,
    mergeableScores,
    32,
    resampleSpacingM,
  );
  const mergedPeakSet = new Set(merged.peakIndices);

  peakIndices = [...merged.peakIndices, ...chicanePeaks].sort((left, right) => left - right);
  peakScores = peakIndices.map(scoreAtPeak);

  const supplementedPeakIndices = fillLargeGapsWithApexPeaks(
    resampled,
    apexCurvature,
    curvatures,
    supplementSignedCurvaturePeaks(resampled, signedCurvatures, peakIndices, 45, 8, 7, 3),
    48,
    36,
    5,
    10,
    4,
  );
  peakScores.length = 0;
  for (const peakIndex of supplementedPeakIndices) {
    if (!peakProfiles.has(peakIndex)) {
      peakProfiles.set(peakIndex, 'sharp');
    }
    peakScores.push(scoreAtPeak(peakIndex));
  }
  peakIndices = [...supplementedPeakIndices];

  for (const kerbIndex of kerbCenterIndices) {
    const radiusSamples = Math.ceil(14 / resampleSpacingM);
    let bestIndex = kerbIndex;
    let bestCurvature = 0;
    for (let offset = -radiusSamples; offset <= radiusSamples; offset += 1) {
      const index = (kerbIndex + offset + resampled.length) % resampled.length;
      const curvature =
        apexCurvature[Math.max(0, Math.min(apexCurvature.length - 1, index - 1))] ?? 0;
      if (curvature > bestCurvature) {
        bestCurvature = curvature;
        bestIndex = index;
      }
    }
    const nearestPeakM = peakIndices.reduce(
      (nearest, peakIndex) => Math.min(nearest, Math.abs(peakIndex - bestIndex) * resampleSpacingM),
      Number.POSITIVE_INFINITY,
    );
    if (bestCurvature >= 2.5 && nearestPeakM >= GLOBAL_MIN_CORNER_SEPARATION_M) {
      peakIndices.push(bestIndex);
      peakScores.push(scoreAtPeak(bestIndex));
      peakProfiles.set(bestIndex, 'sharp');
    }
  }

  const consolidated = consolidateCornerPeaks(
    resampled,
    curvatures,
    signedCurvatures,
    apexCurvature,
    peakIndices,
    peakScores,
    peakProfiles,
  );
  peakIndices = consolidated.peakIndices;
  peakScores = consolidated.peakScores;

  // One sweeper apex per long constant-curvature run that gap passes may miss.
  for (const sweeperCandidate of collectSweeperRunCandidates(
    resampled,
    curvatures,
    signedCurvatures,
  )) {
    const nearest = peakIndices.reduce((min, peak) => {
      return Math.min(min, distanceAlongPolyline(resampled, peak, sweeperCandidate.resampledIndex));
    }, Number.POSITIVE_INFINITY);
    if (nearest >= 45) {
      peakIndices.push(sweeperCandidate.resampledIndex);
      peakProfiles.set(sweeperCandidate.resampledIndex, 'sweeper');
      peakScores.push(scoreAtPeak(sweeperCandidate.resampledIndex));
    }
  }

  const resort = peakIndices
    .map((index, position) => ({ index, score: peakScores[position] ?? 0 }))
    .sort((left, right) => left.index - right.index);
  peakIndices = resort.map((entry) => entry.index);
  peakScores = resort.map((entry) => entry.score);

  const finalConsolidated = consolidateCornerPeaks(
    resampled,
    curvatures,
    signedCurvatures,
    apexCurvature,
    peakIndices,
    peakScores,
    peakProfiles,
    GLOBAL_MIN_CORNER_SEPARATION_M,
  );
  peakIndices = finalConsolidated.peakIndices;
  peakScores = finalConsolidated.peakScores;

  return peakIndices.map((peakIndex, cornerIndex) => {
    let apexIndex = peakIndex;
    const detectionProfile = peakProfiles.get(peakIndex) ?? 'sharp';
    const profileConfig = CURVE_DETECTION_PROFILES[detectionProfile];
    const profileSignals = buildProfileSignals(curvatures, signedCurvatures, detectionProfile);
    const smoothIndex = Math.max(0, Math.min(smoothed.length - 1, peakIndex - 1));
    const searchRadius = Math.max(4, Math.ceil(profileConfig.apexSmoothWindow * 1.5));
    const searchStart = Math.max(0, smoothIndex - searchRadius);
    const searchEnd = Math.min(smoothed.length - 1, smoothIndex + searchRadius);
    let bestCurvature = -1;
    const useDetectedApex = !mergedPeakSet.has(peakIndex);

    if (speedByIndex?.length) {
      const speedSearchStart = Math.max(0, peakIndex - searchRadius);
      const speedSearchEnd = Math.min(resampled.length - 1, peakIndex + searchRadius);
      let minSpeed = Number.POSITIVE_INFINITY;
      for (let index = speedSearchStart; index <= speedSearchEnd; index += 1) {
        const speed =
          speedByIndex[Math.min(speedByIndex.length - 1, index)] ?? Number.POSITIVE_INFINITY;
        if (speed < minSpeed) {
          minSpeed = speed;
          apexIndex = index;
        }
      }
    } else if (useDetectedApex) {
      apexIndex = peakIndex;
    } else {
      for (let index = searchStart; index <= searchEnd; index += 1) {
        const value = profileSignals.apex[index] ?? 0;
        if (value > bestCurvature) {
          bestCurvature = value;
          apexIndex = index + 1;
        }
      }
    }

    const apex = resampled[apexIndex]!;
    const curvature =
      profileSignals.apex[Math.max(0, Math.min(profileSignals.apex.length - 1, apexIndex - 1))] ??
      profileConfig.minLocalCurvatureDeg;
    const prevPeakIndex = cornerIndex > 0 ? peakIndices[cornerIndex - 1] : undefined;
    const nextPeakIndex =
      cornerIndex < peakIndices.length - 1 ? peakIndices[cornerIndex + 1] : undefined;
    const curveType = classifyCurveAtPeak(
      resampled,
      curvatures,
      signedCurvatures,
      peakIndex,
      prevPeakIndex,
      nextPeakIndex,
    );
    const turnNumber = cornerIndex + 1;
    return {
      id: `corner-${turnNumber}`,
      label: `Turn ${turnNumber}`,
      lat: apex.lat,
      lon: apex.lon,
      turnNumber,
      confidence: Math.min(0.98, 0.58 + curvature / 110 + (peakScores[cornerIndex] ?? 0) / 200),
      curveType,
      detectionProfile,
    };
  });
}

function resamplePolylineByFraction(points: GpsCoord[], samples: number): GpsCoord[] {
  if (points.length < 2) {
    return points;
  }

  const cumulative = [0];
  for (let i = 1; i < points.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + haversineM(points[i - 1]!, points[i]!));
  }

  const total = cumulative.at(-1) ?? 0;
  if (total <= 0) {
    return points;
  }

  const result: GpsCoord[] = [];
  let cursor = 0;
  for (let s = 0; s < samples; s += 1) {
    const target = (s / (samples - 1)) * total;
    while (cursor < cumulative.length - 2 && cumulative[cursor + 1]! < target) {
      cursor += 1;
    }
    const start = cumulative[cursor]!;
    const end = cumulative[cursor + 1]!;
    const t = end > start ? (target - start) / (end - start) : 0;
    const a = points[cursor]!;
    const b = points[cursor + 1]!;
    result.push({
      lat: a.lat + (b.lat - a.lat) * t,
      lon: a.lon + (b.lon - a.lon) * t,
    });
  }

  return result;
}

function polylineLengthM(points: GpsCoord[]) {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += haversineM(points[i - 1]!, points[i]!);
  }
  return total;
}

function gpsToCoords(gps: MyChronGpsPoint[]): GpsCoord[] {
  return gps.map((point) => ({ lat: point.lat, lon: point.lon }));
}

function scoreLapCompleteness(gps: MyChronGpsPoint[], durationMs: number): number {
  const lengthM = polylineLengthM(gpsToCoords(gps));
  const durationOk = durationMs >= 15_000 && durationMs <= 120_000 ? 1 : 0.3;
  const closure = gps.length >= 2 ? Math.max(0, 1 - haversineM(gps[0]!, gps.at(-1)!) / 120) : 0;
  const lengthOk = lengthM > 400 && lengthM < 2500 ? 1 : 0.4;
  return durationOk * 0.4 + closure * 0.35 + lengthOk * 0.25;
}

function scoreLineConsistency(reference: GpsCoord[], candidate: GpsCoord[]): number {
  const ref = resamplePolylineByFraction(reference, 64);
  const cand = resamplePolylineByFraction(candidate, 64);
  if (ref.length !== cand.length) {
    return 0;
  }

  const distances = ref.map((point, index) => haversineM(point, cand[index]!));
  const mean = distances.reduce((sum, v) => sum + v, 0) / distances.length;
  return Math.max(0, Math.min(1, 1 - mean / 25));
}

export type ReslicedLapKind = 'flying' | 'out' | 'in' | 'crash';

export type ReslicedLapWindow = ReplayLapWindow & {
  complete: boolean;
  kind: ReslicedLapKind;
};

export type ResliceLapsResult = {
  laps: ReslicedLapWindow[];
  crossingsMs: number[];
};

function gpsCoordFromPoint(point: MyChronGpsPoint): GpsCoord {
  return { lat: point.lat, lon: point.lon };
}

function toLocalMeters(origin: GpsCoord, point: GpsCoord) {
  const latRad = (origin.lat * Math.PI) / 180;
  return {
    x: (((point.lon - origin.lon) * Math.PI) / 180) * Math.cos(latRad) * EARTH_RADIUS_M,
    y: (((point.lat - origin.lat) * Math.PI) / 180) * EARTH_RADIUS_M,
  };
}

export function signedDistanceToStartLineM(
  point: GpsCoord,
  startLine: Pick<StartLineSuggestion, 'pointA' | 'pointB'>,
): number {
  const origin = {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
  const a = toLocalMeters(origin, startLine.pointA);
  const b = toLocalMeters(origin, startLine.pointB);
  const p = toLocalMeters(origin, point);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);

  if (length <= 0) {
    return 0;
  }

  return ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x)) / length;
}

function distanceToStartLineSegmentM(
  point: GpsCoord,
  startLine: Pick<StartLineSuggestion, 'pointA' | 'pointB'>,
): number {
  const origin = {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
  const a = toLocalMeters(origin, startLine.pointA);
  const b = toLocalMeters(origin, startLine.pointB);
  const p = toLocalMeters(origin, point);
  const abX = b.x - a.x;
  const abY = b.y - a.y;
  const lengthSquared = abX * abX + abY * abY;

  if (lengthSquared <= 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  const projection = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * abX + (p.y - a.y) * abY) / lengthSquared),
  );
  const closestX = a.x + abX * projection;
  const closestY = a.y + abY * projection;
  return Math.hypot(p.x - closestX, p.y - closestY);
}

function crossingTimeOnGpsSegment(
  previous: MyChronGpsPoint,
  current: MyChronGpsPoint,
  startLine: StartLineSuggestion,
): number | null {
  const origin = {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
  const a = toLocalMeters(origin, startLine.pointA);
  const b = toLocalMeters(origin, startLine.pointB);
  const s0 = toLocalMeters(origin, gpsCoordFromPoint(previous));
  const s1 = toLocalMeters(origin, gpsCoordFromPoint(current));
  const rd = { x: s1.x - s0.x, y: s1.y - s0.y };
  const qd = { x: b.x - a.x, y: b.y - a.y };
  const denom = rd.x * qd.y - rd.y * qd.x;

  if (Math.abs(denom) < 1e-6) {
    return null;
  }

  const t = ((a.x - s0.x) * qd.y - (a.y - s0.y) * qd.x) / denom;
  const u = ((a.x - s0.x) * rd.y - (a.y - s0.y) * rd.x) / denom;
  const segmentMargin = 0.25;

  if (t < 0 || t > 1 || u < -segmentMargin || u > 1 + segmentMargin) {
    return null;
  }

  const d0 = signedDistanceToStartLineM(gpsCoordFromPoint(previous), startLine);
  const d1 = signedDistanceToStartLineM(gpsCoordFromPoint(current), startLine);
  if (d0 * d1 > 0 && Math.min(Math.abs(d0), Math.abs(d1)) > 4) {
    return null;
  }

  return previous.timecodeMs + t * (current.timecodeMs - previous.timecodeMs);
}

function dedupeCrossingTimes(crossingsMs: number[], mergeWithinMs = 1_500): number[] {
  if (crossingsMs.length === 0) {
    return [];
  }

  const sorted = [...crossingsMs].sort((left, right) => left - right);
  const merged = [sorted[0]!];

  for (let index = 1; index < sorted.length; index += 1) {
    const value = sorted[index]!;
    const last = merged.at(-1)!;

    if (value - last <= mergeWithinMs) {
      merged[merged.length - 1] = (last + value) / 2;
      continue;
    }

    merged.push(value);
  }

  return merged;
}

function estimateMinCrossingGapMs(crossingsMs: number[]): number {
  if (crossingsMs.length < 2) {
    return 45_000;
  }

  const gaps: number[] = [];
  for (let index = 1; index < crossingsMs.length; index += 1) {
    gaps.push(crossingsMs[index]! - crossingsMs[index - 1]!);
  }

  gaps.sort((left, right) => left - right);
  const medianGap = gaps[Math.floor(gaps.length / 2)] ?? 60_000;
  return Math.max(15_000, Math.min(60_000, medianGap * 0.42));
}

export function detectStartLineCrossings(
  gps: MyChronGpsPoint[],
  startLine: StartLineSuggestion,
  options?: {
    minCrossingGapMs?: number;
    rearmDistanceM?: number;
  },
): number[] {
  if (gps.length < 2) {
    return [];
  }

  const rearmDistanceM = options?.rearmDistanceM ?? 18;
  const rawCrossings: number[] = [];
  let armed = true;

  for (let index = 1; index < gps.length; index += 1) {
    const previous = gps[index - 1]!;
    const current = gps[index]!;
    // Rearm against the finite start-line segment. Compact circuits can remain
    // close to the line's infinite extension for a whole lap, which otherwise
    // suppresses every crossing after the first one.
    const distanceM = distanceToStartLineSegmentM(gpsCoordFromPoint(current), startLine);

    if (distanceM >= rearmDistanceM) {
      armed = true;
    }

    const crossingMs = crossingTimeOnGpsSegment(previous, current, startLine);
    if (crossingMs === null || !armed) {
      continue;
    }

    rawCrossings.push(crossingMs);
    armed = false;
  }

  const deduped = dedupeCrossingTimes(rawCrossings);
  const minCrossingGapMs = options?.minCrossingGapMs ?? estimateMinCrossingGapMs(deduped);
  const filtered: number[] = [];

  for (const crossingMs of deduped) {
    if (filtered.length === 0 || crossingMs - filtered.at(-1)! >= minCrossingGapMs) {
      filtered.push(crossingMs);
      continue;
    }
  }

  return filtered;
}

function averageSpeedKph(gps: MyChronGpsPoint[]): number {
  if (gps.length === 0) {
    return 0;
  }

  return gps.reduce((sum, point) => sum + point.speed * 3.6, 0) / gps.length;
}

function medianValue(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((left, right) => left - right);
  return sorted[Math.floor(sorted.length / 2)]!;
}

type GpsSegment = {
  startMs: number;
  endMs: number;
  complete: boolean;
  position: 'leading' | 'between' | 'trailing';
};

type SegmentQuality = {
  completeness: number;
  lineConsistency: number | null;
  pathLengthM: number;
  avgSpeedKph: number;
  sampleCount: number;
};

function splitContinuousGpsAtCrossings(
  session: MyChronSessionExport,
  crossingsMs: number[],
): GpsSegment[] {
  if (session.gps.length < 2) {
    return [];
  }

  const sessionStartMs = session.gps[0]!.timecodeMs;
  const sessionEndMs = session.gps.at(-1)!.timecodeMs;

  if (crossingsMs.length === 0) {
    return [
      {
        startMs: sessionStartMs,
        endMs: sessionEndMs,
        complete: false,
        position: 'leading',
      },
    ];
  }

  const segments: GpsSegment[] = [
    {
      startMs: sessionStartMs,
      endMs: crossingsMs[0]!,
      complete: false,
      position: 'leading',
    },
  ];

  for (let index = 0; index < crossingsMs.length - 1; index += 1) {
    segments.push({
      startMs: crossingsMs[index]!,
      endMs: crossingsMs[index + 1]!,
      complete: true,
      position: 'between',
    });
  }

  const lastCrossingMs = crossingsMs.at(-1)!;
  if (sessionEndMs - lastCrossingMs >= 3_000) {
    segments.push({
      startMs: lastCrossingMs,
      endMs: sessionEndMs,
      complete: false,
      position: 'trailing',
    });
  }

  return segments;
}

function scoreSegmentQuality(
  session: MyChronSessionExport,
  segment: GpsSegment,
  referencePolyline?: GpsCoord[],
): SegmentQuality {
  const gps = gpsPointsInWindow(session.gps, segment.startMs, segment.endMs);
  const durationMs = segment.endMs - segment.startMs;
  const coords = gpsToCoords(gps);

  return {
    completeness: scoreLapCompleteness(gps, durationMs),
    lineConsistency:
      referencePolyline && coords.length >= 2
        ? scoreLineConsistency(referencePolyline, coords)
        : null,
    pathLengthM: polylineLengthM(coords),
    avgSpeedKph: averageSpeedKph(gps),
    sampleCount: gps.length,
  };
}

function medianLongerHalf(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const upperHalf = sorted.slice(Math.floor(sorted.length / 2));
  return medianValue(upperHalf.length > 0 ? upperHalf : sorted);
}

function isCrashSegment(
  durationMs: number,
  durationSec: number,
  baselineFlyingDurationSec: number | null,
  quality: SegmentQuality,
  referenceLengthM: number | null,
  minLapDurationMs: number,
  maxLapDurationMs: number,
): boolean {
  if (durationMs < minLapDurationMs || durationMs > maxLapDurationMs) {
    return true;
  }

  if (baselineFlyingDurationSec === null) {
    return false;
  }

  const veryShort = durationSec < Math.min(22, baselineFlyingDurationSec * 0.55);
  const muchShorter = durationSec < baselineFlyingDurationSec * 0.68;
  const halfLap = referenceLengthM !== null && quality.pathLengthM < referenceLengthM * 0.45;
  const stopped = quality.avgSpeedKph > 0 && quality.avgSpeedKph < 8;

  if (veryShort) {
    return true;
  }

  return muchShorter && (halfLap || stopped || quality.sampleCount < 5);
}

function classifyContinuousGpsSegments(
  session: MyChronSessionExport,
  segments: GpsSegment[],
  referencePolyline: GpsCoord[] | undefined,
  minLapDurationMs: number,
  maxLapDurationMs: number,
): ReslicedLapWindow[] {
  if (segments.length === 0) {
    return [];
  }

  const referenceLengthM =
    referencePolyline && referencePolyline.length >= 2 ? polylineLengthM(referencePolyline) : null;

  const segmentMeta = segments.map((segment, index) => {
    const durationMs = segment.endMs - segment.startMs;
    const quality = scoreSegmentQuality(session, segment, referencePolyline);

    return { segment, index, durationMs, quality };
  });

  const betweenDurationSec = segmentMeta
    .filter(
      (entry) =>
        entry.segment.position === 'between' &&
        entry.durationMs >= minLapDurationMs &&
        entry.durationMs <= maxLapDurationMs,
    )
    .map((entry) => entry.durationMs / 1000);
  const baselineFlyingDurationSec = medianLongerHalf(betweenDurationSec);

  return segmentMeta.map(({ segment, durationMs, quality }, index) => {
    const durationSec = durationMs / 1000;
    let kind: ReslicedLapKind = 'flying';

    if (segment.position === 'trailing') {
      kind = 'in';
    } else if (segment.position === 'leading') {
      kind = 'out';
    } else if (
      isCrashSegment(
        durationMs,
        durationSec,
        baselineFlyingDurationSec,
        quality,
        referenceLengthM,
        minLapDurationMs,
        maxLapDurationMs,
      )
    ) {
      kind = 'crash';
    }

    return {
      lapNumber: index + 1,
      startMs: segment.startMs,
      endMs: segment.endMs,
      durationSec: durationMs / 1000,
      complete: segment.complete,
      kind,
    };
  });
}

export function resliceLapsAtStartLine(
  session: MyChronSessionExport,
  startLine: StartLineSuggestion,
  options?: {
    minLapDurationMs?: number;
    maxLapDurationMs?: number;
    minCrossingGapMs?: number;
    referencePolyline?: GpsCoord[];
  },
): ResliceLapsResult {
  const minLapDurationMs = options?.minLapDurationMs ?? 15_000;
  const maxLapDurationMs = options?.maxLapDurationMs ?? 120_000;
  const crossingsMs = detectStartLineCrossings(session.gps, startLine, {
    minCrossingGapMs: options?.minCrossingGapMs,
  });

  const segments = splitContinuousGpsAtCrossings(session, crossingsMs);
  const laps = classifyContinuousGpsSegments(
    session,
    segments,
    options?.referencePolyline,
    minLapDurationMs,
    maxLapDurationMs,
  );

  return { laps, crossingsMs };
}
